import React, { useState } from 'react';
import { useTheme, THEMES, ThemeMeta } from '../context/ThemeContext';
import './ThemePicker.css';

const ThemePicker: React.FC = () => {
    const { currentTheme, setTheme } = useTheme();
    const [hoveredId, setHoveredId] = useState<string | null>(null);

    return (
        <div className="theme-picker" id="theme-picker">
            <div className="theme-picker-header">
                <span className="theme-picker-icon">🎨</span>
                <div>
                    <h3 className="theme-picker-title">Platform Theme</h3>
                    <p className="theme-picker-subtitle">
                        Choose a visual identity for the entire platform
                    </p>
                </div>
            </div>

            <div className="theme-options">
                {THEMES.map((theme: ThemeMeta) => {
                    const isActive = currentTheme.id === theme.id;
                    const isHovered = hoveredId === theme.id;

                    return (
                        <button
                            key={theme.id}
                            id={`theme-option-${theme.id}`}
                            className={`theme-option ${isActive ? 'active' : ''}`}
                            onClick={() => setTheme(theme.id)}
                            onMouseEnter={() => setHoveredId(theme.id)}
                            onMouseLeave={() => setHoveredId(null)}
                            aria-label={`Switch to ${theme.name} theme`}
                        >
                            {/* Live preview card */}
                            <div
                                className="theme-preview"
                                style={{ background: theme.colors.ivory }}
                            >
                                {/* Mini nav bar */}
                                <div
                                    className="preview-nav"
                                    style={{
                                        background: theme.id === 'midnight-sapphire'
                                            ? 'rgba(22, 36, 71, 0.9)'
                                            : 'rgba(255,255,255,0.85)',
                                        borderBottom: `1px solid ${theme.colors.goldBorder}`,
                                    }}
                                >
                                    <div
                                        className="preview-nav-dot"
                                        style={{ background: theme.colors.gold }}
                                    />
                                    <div className="preview-nav-lines">
                                        <span style={{ background: theme.colors.charcoalMuted }} />
                                        <span style={{ background: theme.colors.charcoalMuted }} />
                                        <span style={{ background: theme.colors.charcoalMuted }} />
                                    </div>
                                </div>

                                {/* Mini content */}
                                <div className="preview-content">
                                    <div
                                        className="preview-heading"
                                        style={{ background: theme.colors.charcoal }}
                                    />
                                    <div
                                        className="preview-subheading"
                                        style={{ background: theme.colors.charcoalMuted }}
                                    />
                                    <div
                                        className="preview-btn"
                                        style={{ background: theme.colors.primaryGradient }}
                                    />
                                </div>

                                {/* Color swatches */}
                                <div className="preview-swatches">
                                    {theme.previewColors.map((color, i) => (
                                        <div
                                            key={i}
                                            className="preview-swatch"
                                            style={{ background: color }}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Label area */}
                            <div className="theme-info">
                                <div className="theme-name-row">
                                    <span className="theme-emoji">{theme.emoji}</span>
                                    <span className="theme-name">{theme.name}</span>
                                    {isActive && (
                                        <span className="theme-active-badge">Active</span>
                                    )}
                                </div>
                                <p className="theme-desc">{theme.description}</p>
                            </div>

                            {/* Active indicator ring */}
                            {isActive && <div className="active-ring" style={{ borderColor: theme.colors.gold }} />}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default ThemePicker;
